import { CheckCircle2, Zap, BarChart2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { SectionEyebrow } from '@/components/ui/SectionEyebrow'
import { Text } from '@/components/ui/Text'

export interface Day0ExplainerStep {
  icon: LucideIcon
  label: string
  body: string
}

const STEPS: readonly Day0ExplainerStep[] = [
  {
    icon: CheckCircle2,
    label: 'Review & reconcile',
    body: 'Your data is checked against public records.',
  },
  {
    icon: Zap,
    label: 'Go live',
    body: 'Entities are verified and activated.',
  },
  {
    icon: BarChart2,
    label: 'Live dashboard',
    body: 'Full ownership graph and filing calendar available.',
  },
] as const

interface Day0PostImportExplainerProps {
  /** Override the eyebrow label — defaults to the client's own "WHAT HAPPENS AFTER IMPORT?" copy. */
  eyebrow?: string
  /** Override the numbered steps — defaults to the client's own import→live→dashboard steps. */
  steps?: readonly Day0ExplainerStep[]
}

export function Day0PostImportExplainer({
  eyebrow = 'WHAT HAPPENS AFTER IMPORT?',
  steps = STEPS,
}: Day0PostImportExplainerProps = {}) {
  return (
    <Card data-testid="day0-post-import-explainer">
      <CardHeader className="py-4">
        <SectionEyebrow>{eyebrow}</SectionEyebrow>
      </CardHeader>
      <CardContent>
        <ol role="list" className="flex flex-col gap-4">
          {steps.map((step, i) => (
            <li key={step.label} className="flex gap-3">
              <div className="shrink-0 flex items-center justify-center size-8 rounded-full bg-primary/10">
                <step.icon size={16} className="text-primary" aria-hidden />
              </div>
              <div className="flex flex-col gap-0.5">
                <Text variant="label-sm" color="default">
                  {i + 1}. {step.label}
                </Text>
                <Text variant="body-sm" color="muted">
                  {step.body}
                </Text>
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}
